import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { History, ArrowRight, User2 } from "lucide-react";
import { toast } from "sonner";

function formatTime(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

function shortVal(v) {
    if (v === null || v === undefined || v === "") return "—";
    if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
    if (typeof v === "object") return "(object)";
    const s = String(v);
    return s.length > 80 ? s.slice(0, 77) + "…" : s;
}

function fieldLabel(e) {
    // Prefer the catalog label stored in meta (richer than raw key)
    if (e.meta?.label) return e.meta.label;
    if (e.field) return e.field.replace(/_/g, " ");
    return null;
}

function summarizeRow(e) {
    if (e.action === "stage_changed") return `Moved from ${e.from_value || "?"} to ${e.to_value || "?"}`;
    if (e.action === "screening_field_updated") {
        const label = fieldLabel(e) || "Screening field";
        return e.to_value === null || e.to_value === undefined
            ? `Cleared ${label}`
            : `Updated ${label}`;
    }
    if (e.summary) return e.summary;
    if (e.action?.endsWith("_field_updated") && e.field) return `Updated ${e.field}`;
    return e.action || "Updated";
}

// Show the old→new chip pair whenever this event is a field-level change.
// Renders "—" for empty/null sides so "cleared" events are legible.
function shouldShowDiff(e) {
    if (!e.action) return false;
    if (e.action === "stage_changed") return true;
    if (e.action.endsWith("_field_updated")) return true;
    return false;
}

export default function ActivityFeed({ targetType, targetId, limit = 200 }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/audit", { params: { target_type: targetType, target_id: targetId, limit } });
                if (!cancelled) setItems(data || []);
            } catch (err) {
                if (!cancelled) toast.error(formatApiError(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [targetType, targetId, limit]);

    return (
        <section data-testid="activity-feed">
            <div className="flex items-center gap-2 mb-4">
                <History className="w-4 h-4 text-ikb" />
                <h3 className="font-display font-bold text-sm uppercase tracking-widest">Activity log</h3>
                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 ml-2">{items.length} event{items.length === 1 ? "" : "s"}</span>
            </div>
            {loading ? (
                <div className="text-sm font-mono text-foreground/80">Loading...</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border p-8 text-center text-sm text-foreground/80">
                    No activity recorded yet. Every edit, stage move, and field change will show up here.
                </div>
            ) : (
                <ol className="border-l-2 border-border ml-3 space-y-3">
                    {items.map((e) => (
                        <li key={e.id} className="relative pl-6">
                            <span className="absolute -left-[7px] top-2 w-3 h-3 bg-ikb border-2 border-white" aria-hidden="true" />
                            <div className="bg-white border border-border p-3">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="font-medium text-sm">{summarizeRow(e)}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/80">{formatTime(e.created_at)}</div>
                                </div>
                                {shouldShowDiff(e) && (
                                    <div className="mt-2 text-xs font-mono text-foreground/80 space-y-1">
                                        {fieldLabel(e) && e.action !== "stage_changed" && (
                                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid={`activity-field-${e.id}`}>
                                                Field: <span className="text-foreground font-bold">{fieldLabel(e)}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Old</span>
                                            <span className="border border-border bg-secondary px-1.5 py-0.5" data-testid={`activity-from-${e.id}`}>{shortVal(e.from_value ?? e.meta?.before)}</span>
                                            <ArrowRight className="w-3 h-3" />
                                            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">New</span>
                                            <span className="border border-ikb text-ikb bg-blue-50 px-1.5 py-0.5" data-testid={`activity-to-${e.id}`}>{shortVal(e.to_value ?? e.meta?.after)}</span>
                                        </div>
                                    </div>
                                )}
                                {e.actor_name && (
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 mt-1 inline-flex items-center gap-1">
                                        <User2 className="w-3 h-3" /> {e.actor_name}{e.actor_role ? ` · ${e.actor_role}` : ""}
                                    </div>
                                )}
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </section>
    );
}
